/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

let glassContext;

const getSvc = (...args) => glassContext.getSvc(...args);

export const initGlassContext = context => (glassContext = context);

export const getGlassContext = () => glassContext;

export const closeAppView = (...args) => glassContext.appController.closeAppView(...args);

export const isDevInstall = () => glassContext.isDevInstall();

export const openAppView = (...args) => glassContext.appController.openAppView(...args);

export const showToast = (...args) => glassContext.appController.showToast(...args);

export const showContextMenu = (...args) => glassContext.appController.showContextMenu(...args);

export const hasCapability = (...args) => glassContext.hasCapability(...args);

export const getServices = () => glassContext.services;

export const getCoreSvc = (...args) => glassContext.getCoreSvc(...args);

export function getProductLocale() {
	if (
		glassContext &&
		glassContext.services &&
		glassContext.services.userProfile &&
		glassContext.services.userProfile.preferences &&
		glassContext.services.userProfile.preferences.productLocale
	) {
		return glassContext.services.userProfile.preferences.productLocale;
	}

	return 'en'; // default
}

export function getTimeZone() {
	if (
		glassContext &&
		glassContext.services &&
		glassContext.services.userProfile &&
		glassContext.services.userProfile.preferences &&
		glassContext.services.userProfile.preferences.timeZoneID
	) {
		return glassContext.services.userProfile.preferences.timeZoneID;
	}

	return 'Europe/London'; // default to get 'GMT' as default timeZone abbreviation
}

export function getContentLocale() {
	if (
		glassContext &&
		glassContext.services &&
		glassContext.services.userProfile &&
		glassContext.services.userProfile.preferences &&
		glassContext.services.userProfile.preferences.contentLocale
	) {
		return glassContext.services.userProfile.preferences.contentLocale;
	}

	return 'en'; // default
}

export function getTimeLocale() {
	return {
		contentLocale: getContentLocale(),
		timeZone: getTimeZone(),
	};
}

export function getTextDirPref() {
	if (
		glassContext &&
		glassContext.services &&
		glassContext.services.userProfile &&
		glassContext.services.userProfile.preferences &&
		glassContext.services.userProfile.preferences.baseTextDirection
	) {
		return glassContext.services.userProfile.preferences.baseTextDirection.toLowerCase();
	}

	return 'auto'; // default
}

export function getCurrentContent() {
	if (
		glassContext &&
		glassContext.appController &&
		glassContext.appController.getCurrentContentView()
	) {
		return glassContext.appController.getCurrentContentView().getContent();
	}

	return {};
}

export function isReadOnly() {
	const isDashboardReadOnly = !(
		!glassContext ||
		!glassContext.appController ||
		!(
			glassContext.appController.getCurrentContentView().isAuthoringMode === false ||
			glassContext.appController.getCurrentContentView.isAuthoringMode === 'false'
		)
	);

	return isDashboardReadOnly;
}

export function track(analyticsData) {
	try {
		const instrumentation = getCoreSvc('.Instrumentation');

		if (instrumentation && instrumentation.enabled) {
			instrumentation.track(analyticsData);
		}
	} catch (e) {
		console.warn('There was an error attempting to track', e);
	}
}

export function loadUserPreferences() {
	return glassContext
		.getSvc('.UserProfile')
		.then(userProfileSvc => ({ ...userProfileSvc.preferences }));
}

export function updateUserPreferences(data) {
	return getSvc('.UserProfile').then(userProfileSvc => {
		Object.assign(userProfileSvc.preferences, data);

		return userProfileSvc.savePreferences(userProfileSvc.preferences);
	});
}

export function eventOn(...args) {
	return getSvc('.Events').then(eventSvc => eventSvc.on(...args));
}

export function eventOff(...args) {
	return getSvc('.Events').then(eventSvc => eventSvc.off(...args));
}

export function eventTrigger(...args) {
	return getSvc('.Events').then(eventSvc => eventSvc.trigger(...args));
}

export function addToMRU(...args) {
	getSvc('.Content').then(contentSvc => {
		contentSvc.addToMRU(...args);
	});
}

export function getMRUList() {
	return getSvc('.Content').then(contentSvc => contentSvc.getMRUList());
}

export function getDataConnectionServiceFactory() {
	return getSvc('.DataConnectionServiceFactory');
}

export function toggleFeatureFlag(feature, value = 'enabled'): void {
	getCoreSvc('.FeatureChecker').addRules({ 'ba-ui-common': { [feature]: value } });
}

export function isFeatureEnabled(feature): boolean {
	return getCoreSvc('.FeatureChecker').checkValue('ba-ui-common', feature, 'enabled');
}
